import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, AlertCircle, Sparkles, Brain, Activity as ActivityIcon, Heart } from "lucide-react";
import Layout from "../components/Layout";
import SurveyEngine from "../components/SurveyEngine";
import CircularProgress from "../components/CircularProgress";
import { api } from "../lib/api";
import { SURVEYS, assessSurvey } from "../data/courseData";
import { CATEGORY_META, tierLabel } from "../lib/survey";

const CAT_ICONS = { mental: Brain, physical: ActivityIcon, emotional: Heart };

export default function SurveyPage() {
  const { step } = useParams();
  const stepNum = parseInt(step, 10);
  const survey = SURVEYS[stepNum];
  const navigate = useNavigate();
  const [done, setDone] = useState(null);

  async function complete({ answers, score, raw_score, normalized_score, categories }) {
    // Persist normalized + category breakdown so the admin/coach view sees the
    // same numbers the user sees. `score` is the normalized 0..100 value.
    await api.post("/surveys", {
      step: stepNum,
      answers,
      score,
      raw_score,
      normalized_score,
      categories,
    });
    setDone({ score, answers, categories });
  }

  const assessment = done ? assessSurvey(stepNum, done.score, done.answers) : null;

  return (
    <Layout hideNav>
      <button onClick={() => navigate(`/journey/${stepNum}`)} className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4" data-testid="back-to-step">
        <ArrowLeft size={16} /> Week {stepNum}
      </button>
      {!done ? (
        <SurveyEngine title={survey.title} subtitle={survey.subtitle} questions={survey.questions} onComplete={complete} />
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5 pb-8" data-testid="survey-assessment">
          <div className="text-center">
            <div className="bps-eyebrow">Assessment</div>
            <h2 className="font-display text-3xl font-light tracking-tighter mt-2">Your reading.</h2>
          </div>

          <div className="bps-card bps-card-accent p-6 flex items-center gap-5" data-testid="assessment-score">
            <CircularProgress value={done.score} size={96} color={assessment.band.color} sublabel={tierLabel(done.score)} />
            <div className="flex-1">
              <div className="bps-eyebrow" style={{ color: assessment.band.color }}>{assessment.band.label}</div>
              <h3 className="font-display text-lg font-medium tracking-tight mt-1 leading-snug">{assessment.band.headline}</h3>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C8FA8] mt-1">
                Normalized · higher is better
              </div>
            </div>
          </div>

          {/* Per-category breakdown */}
          {assessment.categories && (
            <div className="bps-card p-5" data-testid="assessment-categories">
              <div className="bps-eyebrow mb-3">Category breakdown</div>
              <div className="space-y-3">
                {["mental", "physical", "emotional"].map((key) => {
                  const v = assessment.categories[key];
                  const meta = CATEGORY_META[key];
                  const Icon = CAT_ICONS[key];
                  if (v == null) return null;
                  return (
                    <div key={key} data-testid={`cat-${key}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="inline-flex items-center gap-2">
                          <Icon size={13} style={{ color: meta.color }} strokeWidth={1.8} />
                          <span className="text-sm text-white font-display">{meta.label}</span>
                        </div>
                        <span className="font-mono text-sm font-semibold" style={{ color: meta.color }}>
                          {v}<span className="text-[10px] text-[#7C8FA8]">/100</span>
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[#7C8FA8]">{tierLabel(v)}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${v}%` }}
                          transition={{ duration: 0.9, ease: "easeOut", delay: 0.1 }}
                          className="h-full"
                          style={{ background: meta.color }}
                        />
                      </div>
                      <div className="text-[10px] text-[#7C8FA8] mt-1 leading-relaxed">{meta.desc}</div>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#9AAEC6] italic mt-4 leading-relaxed flex items-start gap-2">
                <Sparkles size={11} className="mt-0.5 flex-shrink-0 text-[#22D3EE]" />
                If one category is far lower, that's where the pain is loudest — even if it's not where you feel it.
              </p>
            </div>
          )}

          <div className="bps-card p-5">
            <div className="bps-eyebrow mb-2">What this means</div>
            <p className="text-sm text-white/85 leading-relaxed">{assessment.band.body}</p>
          </div>

          {assessment.concerns.length > 0 && (
            <div className="bps-card p-5" data-testid="assessment-concerns">
              <div className="bps-eyebrow mb-3 flex items-center gap-2"><AlertCircle size={12} /> Pressing patterns</div>
              <ul className="space-y-2.5">
                {assessment.concerns.map((c) => (
                  <li key={c.text} className="flex items-start gap-3 text-sm">
                    <span className="mt-0.5 inline-flex items-center justify-center px-2 h-6 rounded-full text-[10px] font-mono font-semibold" style={{ background: `${assessment.band.color}22`, color: assessment.band.color, border: `1px solid ${assessment.band.color}55` }}>{Math.round(c.norm)}/100</span>
                    <span className="text-white/85 flex-1 leading-snug">{c.text}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#9AAEC6] mt-4 italic flex items-start gap-2"><Sparkles size={12} className="mt-0.5 flex-shrink-0" /> These are the loudest signals. The next practices will work directly with them.</p>
            </div>
          )}

          <button onClick={() => navigate(`/journey/${stepNum}`)} data-testid="continue-to-step" className="w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-semibold shadow-[0_0_24px_rgba(34,211,238,0.35)] hover:-translate-y-0.5 transition-all">
            Continue to next practice
          </button>
        </motion.div>
      )}
    </Layout>
  );
}
